import useScrollDetection from "../../hooks/useScrollDetection";
import { IoArrowBack, IoInformationCircleOutline } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { RiImageAddLine } from "react-icons/ri";
import SwipeAgeUnit from "../../components/swipeComp/SwipeAgeUnit";
import { MdExpandMore } from "react-icons/md";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getDetailUserProfile,
  uploadImageProfile,
} from "../../services/profileApi";
import { IoClose } from "react-icons/io5";
import * as yup from "yup";
import moment from "moment";
import HOC from "../../components/HOC";
import { getLabelConfig } from "../../config/labelConfig";
import PageTemplate from "../../components/common/PageTemplate";
import { GlobalStateContext } from "../../components/context/GlobalStateContext";
import { verifyPurchaseConsultation } from "../../services/transactionApi";
import NotFound from "../../assets/notFound.png";

const validationSchema = yup.object({
  patient_type: yup
    .number()
    .required("Pasien wajib dipilih")
    .oneOf([1, 2], "Pilihan pasien tidak valid")
    .typeError("Pasien harus berupa angka"),
  patient_name: yup
    .string()
    .required("Nama lengkap wajib diisi")
    .min(3, "Nama lengkap minimal 3 karakter")
    .max(100, "Nama lengkap maksimal 100 karakter")
    .matches(/^[a-zA-Z\s]*$/, "Nama hanya boleh mengandung huruf dan spasi"),
  nik: yup
    .string()
    .required("NIK wajib diisi")
    .matches(/^\d{16}$/, "NIK harus terdiri dari 16 digit angka"),
  gender: yup
    .number()
    .required("Jenis kelamin wajib dipilih")
    .oneOf([1, 2], "Jenis kelamin tidak valid")
    .typeError("Jenis kelamin harus berupa angka"),
  age: yup
    .string()
    .required("Usia wajib diisi")
    .matches(/^[0-9]+$/, "Usia harus berupa angka")
    .test("min-age", "Usia minimal 1", (value) => {
      const age = parseInt(value, 10);
      return age >= 1;
    })
    .test("max-age", "Usia maksimal 150", (value) => {
      const age = parseInt(value, 10);
      return age <= 150;
    }),
  age_type: yup
    .number()
    .required("Tipe usia wajib dipilih")
    .oneOf([0, 1], "Tipe usia tidak valid")
    .typeError("Tipe usia harus berupa angka"),
  complaints: yup
    .string()
    .required("Gejala wajib diisi")
    .min(10, "Gejala minimal 10 karakter")
    .max(1000, "Gejala maksimal 1000 karakter")
    .trim(),
  images: yup
    .array()
    .nullable()
    .of(
      yup
        .mixed()
        .test("fileSize", "Ukuran file terlalu besar", (value) => {
          if (!value) return true;
          if (value instanceof File) {
            return value.size <= 10 * 1024 * 1024;
          }
          return true;
        })
        .test("fileType", "Format file tidak didukung", (value) => {
          if (!value) return true;
          if (value instanceof File) {
            return [
              "image/jpeg",
              "image/jpg",
              "image/png",
              "image/gif",
            ].includes(value.type);
          }
          return true;
        }),
    )
    .test("max-files", "Maksimal 5 gambar", (value) => {
      return !value || value.length <= 5;
    }),
});

const commonStyles = {
  fontFamily: '"Montserrat", sans-serif',

  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#d1d5db",
  },

  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9ca3af",
  },

  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },

  "& .MuiInputBase-input": {
    padding: "14px 12px",
    color: "#111827",
    fontSize: "14px",
    fontFamily: '"Montserrat", sans-serif',

    "&::placeholder": {
      color: "#CBCBCB",
      opacity: 1,
      fontFamily: '"Montserrat", sans-serif',
    },
  },

  "&.Mui-disabled": {
    backgroundColor: "#f9fafb",
  },

  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94c679",
  },

  "& .MuiInputBase-input.Mui-disabled": {
    color: "#94c679",
    WebkitTextFillColor: "#94c679",
  },
};

const textFieldStyles = {
  backgroundColor: "white",
  borderRadius: "8px",
  color: "#111827",
  transition: "all 0.3s ease",
  ...commonStyles,
};

const errorTextFieldStyles = {
  ...textFieldStyles,
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#ef4444",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ef4444",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ef4444",
    borderWidth: "1px",
  },
};

const formHelperFieldStyles = {
  width: "100%",
  display: "flex",
  justifyContent: "start",
  margin: "4px 0 0 0",
  fontSize: "12px",
  color: "#ef4444",
  fontFamily: '"Montserrat", sans-serif',
};

const disabledGreenLabelStyle = {
  "& .MuiFormControlLabel-label": {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "14px",
  },
};

const greenRadioStyle = {
  color: "#d1d5db",
  "&.Mui-checked": {
    color: "#94c679",
  },
  "&:hover": {
    backgroundColor: "rgba(148, 198, 121, 0.08)",
  },
  "&.Mui-disabled": {
    color: "#cbd5e1",
  },
  "&.Mui-disabled.Mui-checked": {
    color: "#94c679",
  },
};

const ConsultationDetailForm = () => {
  const navigate = useNavigate();
  const isScrolled = useScrollDetection(5);
  const location = useLocation();
  const { productDetail, doctorData } = location.state || {};
  const label = getLabelConfig();
  const { isMobileView } = useContext(GlobalStateContext);

  const [errorMessage, setErrorMessage] = useState("");
  const [showAgeUnitPicker, setShowAgeUnitPicker] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  const getFieldStyles = (hasError) => {
    return hasError ? errorTextFieldStyles : textFieldStyles;
  };

  const formik = useFormik({
    initialValues: {
      patient_type: 0,
      patient_name: "",
      nik: "",
      gender: 0,
      age: 0,
      age_type: 0,
      complaints: "",
      images: [],
      product_id: productDetail ? productDetail.id : 0,
    },
    validationSchema,
    onSubmit: (value) => {
      navigate("/purchase/checkout/payment-method", {
        state: {
          type: 2,
          checkoutData: value,
        },
      });
    },
    validateOnMount: true,
  });

  console.log("INI ADALAH FORMIK", formik.values);

  const {
    mutate: mutateCheckoutConsultation,
    isPending: isConsultationLoading,
  } = useMutation({
    mutationFn: verifyPurchaseConsultation,
    onSuccess(data) {
      const consultationId = data?.invoice || "";
      if (consultationId) {
        sessionStorage.setItem(
          "waiting_consultation_id",
          consultationId.toString(),
        );
      }
      navigate(`/consultation/waiting`, {
        state: {
          consultationId,
        },
      });
    },
  });

  const { data } = useQuery({
    queryKey: ["detail-user-profile"],
    queryFn: () => getDetailUserProfile(),
    keepPreviousData: false,
  });

  const calculateAgeFromDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth) return { age: 0, ageType: 0 };

    const birthDate = moment(dateOfBirth);
    const today = moment();

    const years = today.diff(birthDate, "years");

    if (years > 0) {
      return { age: years, ageType: 0 };
    } else {
      const months = today.diff(birthDate, "months");
      return { age: months, ageType: 1 };
    }
  };

  const resetFormToInitial = () => {
    formik.setFieldValue("patient_name", "");
    formik.setFieldValue("nik", "");
    formik.setFieldValue("gender", 0);
    formik.setFieldValue("age", 0);
    formik.setFieldValue("age_type", 0);
  };

  useEffect(() => {
    if (
      formik.values.patient_type === 1 &&
      data &&
      data !== null &&
      data !== undefined
    ) {
      formik.setFieldValue("patient_name", data.fullname);
      formik.setFieldValue("nik", data.nik);
      formik.setFieldValue("gender", data.gender);

      if (data.date_of_birth) {
        const { age, ageType } = calculateAgeFromDateOfBirth(
          data.date_of_birth,
        );
        formik.setFieldValue("age", age);
        formik.setFieldValue("age_type", ageType);
      } else {
        formik.setFieldValue("age", 0);
        formik.setFieldValue("age_type", 0);
      }
    } else if (formik.values.patient_type === 2) {
      resetFormToInitial();
    }
  }, [formik.values.patient_type, data]);

  const getAgeUnitLabel = (value) => {
    switch (value) {
      case 0:
        return "Tahun";
      case 1:
        return "Bulan";
      default:
        return "Tahun";
    }
  };

  const { mutate: mutateUploadImage } = useMutation({
    mutationFn: uploadImageProfile,
    onSuccess: (data) => {
      formik.setFieldValue("images", [
        ...(formik.values.images || []),
        data.image_url,
      ]);
    },
    onMutate: () => setErrorMessage(""),
    onError: (err) =>
      setErrorMessage(err.message.id ? err.message.id : err.message),
  });

  const handleRemoveImage = (indexToRemove) => {
    const newImages = formik.values.images.filter(
      (_, index) => index !== indexToRemove,
    );
    formik.setFieldValue("images", newImages);
  };

  const handleImageError = (index) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  useEffect(() => {
    setImageErrors({});
  }, [formik.values.images]);

  const content = (
    <div className="bg-white relative">
      <div className="flex flex-row justify-center h-screen">
        <div className="w-full lg:w-1/2 flex justify-center lg:items-center px-0 lg:px-12 text-base">
          <div className="rounded-md p-3 lg:p-8 w-full h-fit">
            {isMobileView && (
              <div
                className={`bg-white fixed z-[9999] top-0 left-0 px-4 py-3 w-full flex items-center transition-all duration-300 ${
                  isScrolled ? "shadow-md rounded-b-3xl" : ""
                }`}
              >
                <div className="flex flex-col w-full gap-4">
                  <div className="w-full flex items-center gap-2 lg:hidden text-xl">
                    <div
                      onClick={() => navigate(-1)}
                      className="p-1 hover:bg-gray-200 rounded-full cursor-pointer"
                    >
                      <IoArrowBack className="text-gray-500" size={20} />
                    </div>
                    <p className="font-semibold text-slate-700">
                      Detail Konsultasi
                    </p>
                  </div>
                </div>
              </div>
            )}

            {doctorData &&
            doctorData !== undefined &&
            doctorData.is_available ? (
              <div className="pt-12 pb-24">
                <form onSubmit={formik.handleSubmit}>
                  <div className="space-y-5">
                    <div>
                      <p>Pasien</p>
                      <FormControl
                        error={
                          formik.touched.patient_type &&
                          Boolean(formik.errors.patient_type)
                        }
                      >
                        <RadioGroup
                          row
                          value={formik.values.patient_type}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "patient_type",
                              Number(e.target.value),
                            )
                          }
                          onBlur={formik.handleBlur}
                          name="patient_type"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontFamily: '"Montserrat", sans-serif',
                              fontSize: "14px",
                            },
                          }}
                        >
                          <FormControlLabel
                            value={1}
                            control={<Radio sx={greenRadioStyle} />}
                            label="Saya sendiri"
                          />
                          <FormControlLabel
                            value={2}
                            control={<Radio sx={greenRadioStyle} />}
                            label="Orang lain"
                          />
                        </RadioGroup>
                        {formik.touched.patient_type &&
                          formik.errors.patient_type && (
                            <FormHelperText sx={formHelperFieldStyles}>
                              {formik.errors.patient_type}
                            </FormHelperText>
                          )}
                      </FormControl>
                    </div>

                    {formik.values.patient_type !== 0 && (
                      <>
                        <div>
                          <p>Nama Pasien</p>
                          <TextField
                            disabled={formik.values.patient_type === 1}
                            fullWidth
                            placeholder="Masukkan nama lengkap"
                            value={formik.values.patient_name}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "patient_name",
                                e.target.value,
                              )
                            }
                            onBlur={formik.handleBlur}
                            name="patient_name"
                            helperText={
                              formik.touched.patient_name &&
                              formik.errors.patient_name
                            }
                            error={
                              formik.touched.patient_name &&
                              Boolean(formik.errors.patient_name)
                            }
                            InputProps={{
                              sx: getFieldStyles(
                                formik.errors.patient_name &&
                                  formik.touched.patient_name,
                              ),
                            }}
                            FormHelperTextProps={{
                              sx: formHelperFieldStyles,
                            }}
                          />
                        </div>

                        <div>
                          <p>NIK Pasien</p>
                          <TextField
                            disabled={formik.values.patient_type === 1}
                            fullWidth
                            placeholder="Masukkan 16 digit NIK Pasien"
                            value={formik.values.nik}
                            onChange={(e) => {
                              const val = e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 16);
                              formik.setFieldValue("nik", val);
                            }}
                            onBlur={formik.handleBlur}
                            name="nik"
                            inputProps={{
                              inputMode: "numeric",
                              maxLength: 16,
                            }}
                            helperText={
                              formik.touched.nik && formik.errors.nik
                                ? formik.errors.nik
                                : formik.values.nik?.length > 0
                                  ? `${formik.values.nik?.length}/16 digit`
                                  : ""
                            }
                            error={
                              formik.touched.nik && Boolean(formik.errors.nik)
                            }
                            InputProps={{
                              sx: getFieldStyles(
                                formik.errors.nik && formik.touched.nik,
                              ),
                            }}
                            FormHelperTextProps={{
                              sx: {
                                ...formHelperFieldStyles,
                                color:
                                  formik.touched.nik && formik.errors.nik
                                    ? "#ef4444"
                                    : "#9ca3af",
                              },
                            }}
                          />
                        </div>

                        <div>
                          <p>Jenis Kelamin</p>
                          <FormControl
                            error={
                              formik.touched.gender &&
                              Boolean(formik.errors.gender)
                            }
                          >
                            <RadioGroup
                              row
                              value={formik.values.gender}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "gender",
                                  Number(e.target.value),
                                )
                              }
                              onBlur={formik.handleBlur}
                              name="gender"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  fontFamily: '"Montserrat", sans-serif',
                                  fontSize: "14px",
                                },
                              }}
                            >
                              <FormControlLabel
                                value={1}
                                control={<Radio sx={greenRadioStyle} />}
                                sx={disabledGreenLabelStyle}
                                label="Laki-laki"
                                disabled={formik.values.patient_type === 1}
                              />
                              <FormControlLabel
                                value={2}
                                control={<Radio sx={greenRadioStyle} />}
                                sx={disabledGreenLabelStyle}
                                label="Perempuan"
                                disabled={formik.values.patient_type === 1}
                              />
                            </RadioGroup>
                            {formik.touched.gender && formik.errors.gender && (
                              <FormHelperText sx={formHelperFieldStyles}>
                                {formik.errors.gender}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </div>

                        <div>
                          <p>Usia</p>
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <TextField
                                fullWidth
                                disabled={formik.values.patient_type === 1}
                                placeholder="Masukkan umur"
                                value={formik.values.age}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (!isNaN(Number(value)) && value !== "") {
                                    formik.setFieldValue("age", Number(value));
                                  } else if (value === "") {
                                    formik.setFieldValue("age", "");
                                  }
                                }}
                                onBlur={formik.handleBlur}
                                name="age"
                                helperText={
                                  formik.touched.age && formik.errors.age
                                }
                                error={
                                  formik.touched.age &&
                                  Boolean(formik.errors.age)
                                }
                                InputProps={{
                                  sx: getFieldStyles(
                                    formik.errors.age && formik.touched.age,
                                  ),
                                }}
                                FormHelperTextProps={{
                                  sx: formHelperFieldStyles,
                                }}
                              />
                            </div>

                            <div className="w-36">
                              <button
                                type="button"
                                onClick={() => {
                                  if (formik.values.patient_type !== 1) {
                                    setShowAgeUnitPicker(true);
                                  }
                                }}
                                className={`${formik.values.patient_type !== 1 ? "text-gray-700 hover:bg-gray-50" : "text-slate-300"} w-full h-[48px] px-4 border border-gray-300 rounded-lg bg-white font-medium flex items-center justify-between transition-colors`}
                              >
                                <span>
                                  {getAgeUnitLabel(formik.values.age_type)}
                                </span>
                                <MdExpandMore
                                  size={20}
                                  className={`${formik.values.patient_type !== 1 ? "text-slate-500" : "text-slate-300"}`}
                                />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div>
                          <p>Keluhan</p>
                          <TextField
                            placeholder="Tuliskan keluhan yang dirasakan"
                            value={formik.values.complaints}
                            multiline
                            minRows={4}
                            maxRows={6}
                            onChange={(e) => {
                              formik.setFieldValue(
                                "complaints",
                                e.target.value,
                              );
                            }}
                            onBlur={formik.handleBlur}
                            name="complaints"
                            fullWidth
                            helperText={
                              formik.touched.complaints &&
                              formik.errors.complaints
                            }
                            error={
                              formik.touched.complaints &&
                              Boolean(formik.errors.complaints)
                            }
                            sx={{
                              ...getFieldStyles(
                                formik.errors.complaints &&
                                  formik.touched.complaints,
                              ),
                              "& .MuiOutlinedInput-root": {
                                padding: 0,
                                borderRadius: "8px",
                                "& textarea": {
                                  padding: "12px 14px",
                                  fontFamily: '"Montserrat", sans-serif',
                                  fontSize: "14px",
                                  lineHeight: "1.5",
                                },
                              },
                            }}
                            FormHelperTextProps={{
                              sx: formHelperFieldStyles,
                            }}
                          />
                        </div>

                        {formik.values.images === "" && (
                          <label
                            htmlFor="dropzone-file1"
                            className="h-[40px] border border-secondary flex items-center justify-center gap-2 bg-white text-secondary w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryActive transform duration-200 hover:scale-100 disabled:bg-slate-400 disabled:cursor-disabled"
                          >
                            <RiImageAddLine size={16} />
                            <p>Tambah Gambar</p>
                            <input
                              id="dropzone-file1"
                              type="file"
                              accept="image/jpeg, image/jpg, image/png"
                              onChange={(e) =>
                                mutateUploadImage(e.target.files[0])
                              }
                              hidden
                            />
                          </label>
                        )}

                        <div className="space-y-2">
                          {!formik.values.images ||
                          formik.values.images.length === 0 ? (
                            <label
                              htmlFor="dropzone-file1"
                              className="h-[40px] border border-secondary flex items-center justify-center gap-2 bg-white text-secondary w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryActive transform duration-200 hover:scale-100 cursor-pointer"
                            >
                              <RiImageAddLine size={16} />
                              <p>Tambah Gambar</p>
                              <input
                                id="dropzone-file1"
                                type="file"
                                accept="image/jpeg, image/jpg, image/png"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    mutateUploadImage(file);
                                  }
                                  e.target.value = "";
                                }}
                                hidden
                              />
                            </label>
                          ) : (
                            <div className="space-y-3">
                              <div className="grid grid-cols-3 gap-2">
                                {formik.values.images.map((imageUrl, index) => (
                                  <div
                                    key={index}
                                    className="relative rounded-lg border border-slate-200 overflow-hidden aspect-square group"
                                  >
                                    {!imageErrors[index] ? (
                                      <img
                                        src={imageUrl}
                                        alt={`preview-${index}`}
                                        className="w-full h-full object-cover"
                                        onError={() => handleImageError(index)}
                                      />
                                    ) : (
                                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                        <IoInformationCircleOutline
                                          size={24}
                                          className="text-gray-400"
                                        />
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      className="absolute top-1 right-1 shadow-md bg-white rounded-full hover:bg-gray-100 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={() => handleRemoveImage(index)}
                                    >
                                      <IoClose
                                        size={16}
                                        className="text-gray-600"
                                      />
                                    </button>
                                  </div>
                                ))}

                                {formik.values.images.length < 5 && (
                                  <label
                                    htmlFor={`dropzone-file-${formik.values.images.length}`}
                                    className="border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center aspect-square cursor-pointer hover:bg-gray-50 transition-colors"
                                  >
                                    <RiImageAddLine
                                      size={24}
                                      className="text-gray-400"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                      Tambah
                                    </p>
                                    <input
                                      id={`dropzone-file-${formik.values.images.length}`}
                                      type="file"
                                      accept="image/jpeg, image/jpg, image/png"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          mutateUploadImage(file);
                                        }
                                        e.target.value = "";
                                      }}
                                      hidden
                                    />
                                  </label>
                                )}
                              </div>

                              {formik.values.images.length < 5 && (
                                <label
                                  htmlFor="dropzone-file-mobile"
                                  className="h-[40px] border border-secondary flex items-center justify-center gap-2 bg-white text-secondary w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryActive transform duration-200 hover:scale-100 cursor-pointer lg:hidden"
                                >
                                  <RiImageAddLine size={16} />
                                  <p>Tambah Gambar Lainnya</p>
                                  <input
                                    id="dropzone-file-mobile"
                                    type="file"
                                    accept="image/jpeg, image/jpg, image/png"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        mutateUploadImage(file);
                                      }
                                      e.target.value = "";
                                    }}
                                    hidden
                                  />
                                </label>
                              )}
                            </div>
                          )}

                          {formik.touched.images && formik.errors.images && (
                            <FormHelperText sx={formHelperFieldStyles}>
                              {typeof formik.errors.images === "string"
                                ? formik.errors.images
                                : "Format gambar tidak valid"}
                            </FormHelperText>
                          )}

                          <p className="text-sm italic text-gray-400">
                            Format: JPG, PNG, GIF. Maksimal 5 gambar. Ukuran
                            maksimal 10MB per gambar.
                          </p>
                        </div>
                      </>
                    )}

                    {productDetail && productDetail !== undefined ? (
                      <>
                        <p className="mb-1">
                          Ingin konsultasi mengenai obat ini:
                        </p>
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-16 shadow-sm object-cover rounded-xl overflow-hidden border border-gray-200 shrink-0">
                            <img
                              src={productDetail.media[0]}
                              alt={productDetail.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-700">
                              {productDetail.name}
                            </p>
                            <p className="text-sm text-slate-500">
                              {productDetail.indication}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : null}

                    {errorMessage && (
                      <div className="w-full flex justify-end mt-2">
                        <div className="flex items-center gap-2 bg-red-50 border border-red-200 py-2 px-3 rounded-md w-full">
                          <IoInformationCircleOutline
                            className="text-red-500 shrink-0"
                            size={16}
                          />
                          <p className="text-sm text-red-700">{errorMessage}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            ) : (
              <div className="min-h-[calc(100vh-64px)] w-full flex items-center justify-center">
                <div className="flex flex-col items-center justify-center text-slate-700 px-4">
                  <img src={NotFound} alt="Not Found" className="w-1/2 mb-3" />
                  <h1 className="font-semibold text-lg text-center">
                    Belum ada dokter yang tersedia
                  </h1>
                  <p className="text-center text-md mt-2">
                    {doctorData &&
                      doctorData !== undefined &&
                      doctorData.consultation_fallback}
                  </p>
                  <button
                    onClick={() => {
                      navigate("/");
                    }}
                    className="h-[40px] mt-4 border border-secondary text-white bg-secondary flex items-center gap-2 justify-center w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline transform duration-200 hover:scale-100"
                  >
                    Kembali ke Beranda
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {doctorData && doctorData !== undefined && doctorData.is_available && (
        <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[99] max-h-[70vh] flex flex-col shadow-2xl">
          <div className="w-full rounded-t-3xl flex items-center gap-2 px-3 py-4 text-base">
            <button
              type="button"
              onClick={() => {
                if (label.brandName === "Orlin") {
                  mutateCheckoutConsultation({
                    ...formik.values,
                    fee: 0,
                    total_transaction: 0,
                  });
                } else {
                  formik.handleSubmit();
                }
              }}
              disabled={!formik.isValid || isConsultationLoading}
              className="h-[40px] border border-secondary flex items-center justify-center bg-secondary text-white w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryActive transform duration-200 hover:scale-100 disabled:border-slate-300 disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              {isConsultationLoading ? (
                <p className="animate-pulse">Memproses...</p>
              ) : (
                <>
                  {label.brandName === "Orlin"
                    ? "Mulai Konsultasi"
                    : "Lanjut Pembayaran"}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <SwipeAgeUnit
        open={showAgeUnitPicker}
        onClose={() => setShowAgeUnitPicker(false)}
        value={formik.values.age_type}
        onChange={(value) => {
          formik.setFieldValue("age_type", value);
        }}
        title="Pilih Satuan Usia"
      />
    </div>
  );

  if (isMobileView) return content;

  return <PageTemplate mainComponent={content} />;
};

export default HOC(ConsultationDetailForm, "required-auth");
